import { Injectable, Signal, signal } from '@angular/core'
import { IToast, IToastMessage } from '@interfaces/i-toast'
import { TMessage } from '@ttypes/t-message'

@Injectable({
  providedIn: 'root',
})

export class ToastService {
  private readonly AUTO_DISMISS_DEFAULT = 3000
  private _toasts = signal<IToast[]>([])
  toasts: Signal<IToast[]> = this._toasts.asReadonly()

  // PUBLIC METHODS

  /**
   * Dismiss a toast message
   * @param {number} index The index of the toast to dismiss
   */
  dismiss(index: number) {
    this._toasts.update(toasts => {
      toasts.splice(index, 1)
      return toasts
    })
  }

  /**
   * Show a toast message with a given type
   * Toast messages with autoDismiss set to false do not have a button to clear
   * Toast messages with autoDismiss set to true will self dismiss after 3 seconds (default) unless a duration is supplied
   * @param {TMessage} type
   * @param {IToastMessage} message
   * @param {string} [message.code] (optional) The message i18n request code
   * @param {string} message.default The message default value
   * @param {boolean} [autoDismiss] (optional) If the message is to auto dismiss - defaults to false
   * @param {number} [duration] (optional) In milliseconds - defaults to 0
   */
  show(type: TMessage, message: IToastMessage, autoDismiss: boolean = false, duration: number = 0): void {
    this.showMessage(type, message, autoDismiss, duration)
  }

  /**
   * Show a toast error message
   * Toast messages with autoDismiss set to false do not have a button to clear
   * Toast messages with autoDismiss set to true will self dismiss after 3 seconds (default) unless a duration is supplied
   * @param {IToastMessage} message
   * @param {string} [message.code] (optional) The message i18n request code
   * @param {string} message.default The message default value
   * @param {boolean} [autoDismiss] (optional) If the message is to auto dismiss - defaults to false
   * @param {number} [duration] (optional) In milliseconds - defaults to 0
   */
  showError(message: IToastMessage, autoDismiss: boolean = false, duration: number = 0): void {
    this.showMessage('error', message, autoDismiss, duration)
  }

  /**
   * Show a toast success message
   * Toast messages with autoDismiss set to false do not have a button to clear
   * Toast messages with autoDismiss set to true will self dismiss after 3 seconds (default) unless a duration is supplied
   * @param {IToastMessage} message
   * @param {string} [message.code] (optional) The message i18n request code
   * @param {string} message.default The message default value
   * @param {boolean} [autoDismiss] (optional) If the message is to auto dismiss - defaults to false
   * @param {number} [duration] (optional) In milliseconds - defaults to 0
   */
  showSuccess(message: IToastMessage, autoDismiss: boolean = false, duration: number = 0): void {
    this.showMessage('success', message, autoDismiss, duration)
  }

  /**
   * Show a toast info message
   * Toast messages with autoDismiss set to false do not have a button to clear
   * Toast messages with autoDismiss set to true will self dismiss after 3 seconds (default) unless a duration is supplied
   * @param {IToastMessage} message
   * @param {string} [message.code] (optional) The message i18n request code
   * @param {string} message.default The message default value
   * @param {boolean} [autoDismiss] (optional) If the message is to auto dismiss - defaults to false
   * @param {number} [duration] (optional) In milliseconds - defaults to 0
   */
  showInfo(message: IToastMessage, autoDismiss: boolean = false, duration: number = 0): void {
    this.showMessage('info', message, autoDismiss, duration)
  }

  /**
   * Show a toast warning message
   * Toast messages with autoDismiss set to false do not have a button to clear
   * Toast messages with autoDismiss set to true will self dismiss after 3 seconds (default) unless a duration is supplied
   * @param {IToastMessage} message
   * @param {string} [message.code] (optional) The message i18n request code
   * @param {string} message.default The message default value
   * @param {boolean} [autoDismiss] (optional) If the message is to auto dismiss - defaults to false
   * @param {number} [duration] (optional) In milliseconds - defaults to 0
   */
  showWarn(message: IToastMessage, autoDismiss: boolean = false, duration: number = 0): void {
    this.showMessage('warn', message, autoDismiss, duration)
  }


  // PRIVATE METHODS

  /**
   * Show a toast message with a given type
   * Checks to see if a toast with the same message already exists in order to prevent duplication spamming
   * @param {TMessage} type
   * @param {IToastMessage} message
   * @param {string} [message.code] (optional) The message i18n request code
   * @param {string} message.default The message default value
   * @param {boolean} [autoDismiss] (optional) If the message is to auto dismiss - defaults to false
   * @param {number} [duration] (optional) In milliseconds - defaults to 0
   */
  private showMessage(type: TMessage, message: IToastMessage, autoDismiss: boolean = false, duration: number = 0): void {
    
    duration = autoDismiss ? duration || this.AUTO_DISMISS_DEFAULT : 0

    const toast: IToast = { type, message, duration }

    const exists: boolean = this._toasts().some(
      _toast => _toast.message.default === toast.message.default,
    )
    if (exists) return

    if (duration) this.autoDismiss(toast, duration)

    this._toasts.update(toasts => [...toasts, toast])
  }

  /**
   * Auto closes a toast with a duration once its timeout is reached
   * @param {IToast} toast
   * @param {number} duration In milliseconds
   */
  private autoDismiss(toast: IToast, duration: number): void {
    const timeOut = setTimeout(() => {
      const index: number = this._toasts().findIndex(_toast => _toast === toast)
      if (index >= 0) this.dismiss(index)
      clearTimeout(timeOut)
    }, duration)
  }

}
